import { useEffect } from 'react';
import { initialSsrTitle } from '@/lib/initial-ssr-meta';
import { 
  MetaConfig, 
  BreadcrumbItem, 
  getCanonicalUrl 
} from '@shared/seo-config';
import {
  removeOwnedJsonLdScripts,
  replaceOwnedJsonLdScripts,
} from './seo-manager-scripts';

interface SeoManagerProps {
  meta: MetaConfig;
  breadcrumbs?: BreadcrumbItem[];
  jsonLd?: object | object[];
  /** Catalog/landing pages use the server's CMS override on client navigation too. */
  syncCms?: boolean;
}

const BASE_URL = 'https://www.universo-oro.it';

function resolveCanonical(canonical: string | undefined): string {
  if (!canonical) {
    return getCanonicalUrl(window.location.pathname, BASE_URL);
  }
  return canonical.startsWith('http') ? canonical : `${BASE_URL}${canonical}`;
}

export function SeoManager({ meta, breadcrumbs, jsonLd, syncCms = false }: SeoManagerProps) {
  useEffect(() => {
    const ownedScripts: HTMLScriptElement[] = [];

    // Update document title
    const title = initialSsrTitle(window.location.pathname) || meta.title;
    document.title = title;
    
    // Update or create meta tags
    updateMetaTag('description', meta.description);
    if (meta.keywords) {
      updateMetaTag('keywords', meta.keywords);
    }
    
    // Open Graph tags
    updateMetaTag('og:title', title, 'property');
    updateMetaTag('og:description', meta.description, 'property');
    updateMetaTag('og:type', meta.ogType || 'website', 'property');
    updateMetaTag('og:url', getCanonicalUrl(window.location.pathname, BASE_URL), 'property');
    updateMetaTag('og:locale', 'it_IT', 'property');
    updateMetaTag('og:site_name', 'Universo Oro', 'property');
    
    if (meta.ogImage) {
      const imageUrl = meta.ogImage.startsWith('http') ? meta.ogImage : `${BASE_URL}${meta.ogImage}`;
      updateMetaTag('og:image', imageUrl, 'property');
      updateMetaTag('og:image:secure_url', imageUrl, 'property');
      if (meta.ogImageWidth) {
        updateMetaTag('og:image:width', meta.ogImageWidth.toString(), 'property');
      }
      if (meta.ogImageHeight) {
        updateMetaTag('og:image:height', meta.ogImageHeight.toString(), 'property');
      }
      updateMetaTag('og:image:type', 'image/jpeg', 'property');
    }
    
    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image', 'name');
    updateMetaTag('twitter:title', title, 'name');
    updateMetaTag('twitter:description', meta.description, 'name');
    if (meta.ogImage) {
      const imageUrl = meta.ogImage.startsWith('http') ? meta.ogImage : `${BASE_URL}${meta.ogImage}`;
      updateMetaTag('twitter:image', imageUrl, 'name');
    }
    
    // Canonical URL
    if (meta.canonical === '') {
      document.querySelector('link[rel="canonical"]')?.remove();
    } else {
      updateCanonicalLink(resolveCanonical(meta.canonical));
    }
    
    // Robots
    if (meta.noIndex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      updateMetaTag('robots', 'index, follow');
    }
    
    // JSON-LD structured data
    replaceOwnedJsonLdScripts(breadcrumbs, jsonLd, ownedScripts, BASE_URL);
    
    // Cleanup on unmount
    return () => {
      removeOwnedJsonLdScripts(ownedScripts);
    };
  }, [meta, breadcrumbs, jsonLd]);

  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  useEffect(() => {
    if (!syncCms || !pathname || meta.noIndex) return;
    const controller = new AbortController();
    void (async () => {
      try {
        const response = await fetch(`/api/crm/cms/seo/page/${encodeURIComponent(pathname)}`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`SEO CMS lookup failed: ${response.status}`);
        const cms = await response.json() as {
          title?: string; description?: string; canonicalUrl?: string; robotsDirective?: string;
        } | null;
        if (!cms || controller.signal.aborted || window.location.pathname !== pathname) return;
        if (cms.title) {
          document.title = cms.title;
          updateMetaTag('og:title', cms.title, 'property');
        }
        if (cms.description) {
          updateMetaTag('description', cms.description);
          updateMetaTag('og:description', cms.description, 'property');
        }
        if (cms.canonicalUrl) updateCanonicalLink(resolveCanonical(cms.canonicalUrl));
        if (cms.robotsDirective) updateMetaTag('robots', cms.robotsDirective);
      } catch (error) {
        if (!controller.signal.aborted) console.warn('[SEO] CMS metadata unavailable:', error);
      }
    })();
    return () => controller.abort();
  }, [pathname, syncCms, meta.noIndex]);
  
  return null;
}

function updateMetaTag(name: string, content: string, attribute: 'name' | 'property' = 'name') {
  let meta = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
  
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, name);
    document.head.appendChild(meta);
  }
  
  meta.content = content;
}

function updateCanonicalLink(href: string) {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  
  link.href = href;
}

// Hook for easy page-level SEO management
export function useSeo(meta: MetaConfig, breadcrumbs?: BreadcrumbItem[], jsonLd?: object | object[]) {
  useEffect(() => {
    const ownedScripts: HTMLScriptElement[] = [];

    // Same logic as SeoManager component
    document.title = meta.title;
    
    updateMetaTag('description', meta.description);
    if (meta.keywords) {
      updateMetaTag('keywords', meta.keywords);
    }
    
    updateMetaTag('og:title', meta.title, 'property');
    updateMetaTag('og:description', meta.description, 'property');
    updateMetaTag('og:type', meta.ogType || 'website', 'property');
    updateMetaTag('og:url', getCanonicalUrl(window.location.pathname, BASE_URL), 'property');
    updateMetaTag('og:locale', 'it_IT', 'property');
    updateMetaTag('og:site_name', 'Universo Oro', 'property');
    
    if (meta.ogImage) {
      const imageUrl = meta.ogImage.startsWith('http') ? meta.ogImage : `${BASE_URL}${meta.ogImage}`;
      updateMetaTag('og:image', imageUrl, 'property');
      updateMetaTag('og:image:secure_url', imageUrl, 'property');
      if (meta.ogImageWidth) {
        updateMetaTag('og:image:width', meta.ogImageWidth.toString(), 'property');
      }
      if (meta.ogImageHeight) {
        updateMetaTag('og:image:height', meta.ogImageHeight.toString(), 'property');
      }
      updateMetaTag('og:image:type', 'image/jpeg', 'property');
    }
    
    updateMetaTag('twitter:card', 'summary_large_image', 'name');
    updateMetaTag('twitter:title', meta.title, 'name');
    updateMetaTag('twitter:description', meta.description, 'name');
    if (meta.ogImage) {
      const imageUrl = meta.ogImage.startsWith('http') ? meta.ogImage : `${BASE_URL}${meta.ogImage}`;
      updateMetaTag('twitter:image', imageUrl, 'name');
    }
    
    updateCanonicalLink(resolveCanonical(meta.canonical));
    
    if (meta.noIndex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      updateMetaTag('robots', 'index, follow');
    }
    
    replaceOwnedJsonLdScripts(breadcrumbs, jsonLd, ownedScripts, BASE_URL);
    
    return () => {
      removeOwnedJsonLdScripts(ownedScripts);
    };
  }, [meta, breadcrumbs, jsonLd]);
}

// Pre-built page SEO configs
export { META_TEMPLATES, generateBreadcrumbs, generateWatchMeta, generateJewelryMeta, generateInvestmentMeta } from '@shared/seo-config';
